import "./APIKeyEditView.scss";
import _ from "lodash";
import ImagePreview from "@/components/Common/ImagePreview/ImagePreview.vue";
import InfoDialog from "@/components/Common/InfoDialog/InfoDialog.vue";
import TreeViewSelection from "@/components/Common/TreeViewSelection/TreeViewSelection.vue";
import { confirmWarningConfig } from "@/configuration/confirmConfigurations";
import {
  destroyFile,
  initGlobalCopyToClipboard,
  escapeHTML,
  getTokenHeader
} from "@/helpers/commonHelper";
import FallbackPictureCopyrightEditView from "@/components/Common/FallbackPictureCopyrightEditView/FallbackPictureCopyrightEditView.vue";
import draggable from "vuedraggable";
import ColorPickerButton from "@/components/Common/ColorPickerButton/ColorPickerButton.vue";
import LogOverview from "@/components/Common/LogOverview/LogOverview.vue";
import VuePrism from "vue-prism";
import "prismjs/themes/prism-okaidia.min.css";
import {
  getListHTML,
  getTeaserHTML,
  getDetailHTML,
  getNewListHTML,
  getNewTeaserHTML,
  getNewContentTeaserHTML,
  getNewDetailHTML
} from "@/helpers/poiHelper.js";

const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);
import axios from "axios";

export default {
  data: () => ({
    categoryGroupText: "",
    categoryTypeText: "",
    mappingFieldText: "",
    selectedMappingFields: [],
    categoryText: "",
    featureText: "",
    preFilterExcludeCategoryGroupText: "",
    showNotepad: false,
    dragging: false,
    isLoading: false,
    eventSearch: null,
    foundEvents: [],
    newsCardStyles: [
      { value: "simple", title: "Simpel" },
      { value: "standard", title: "Standard" },
      { value: "grid", title: "Grid" },
      { value: "list", title: "Liste" }
    ],
    apiKeyPermissions: [
      { value: "edit", title: "Bearbeiten" },
      { value: "view", title: "Sehen" },
      { value: "none", title: "Keine" }
    ],
    sortDirection: [
      { value: "asc", title: "Aufsteigend" },
      { value: "desc", title: "Absteigend" }
    ],
    sortProperties: [
      { value: "sortTitle", title: "Titel" },
      { value: "-x-open-close", title: "Geöffnet/Geschlossen" },
      { value: "random", title: "Zufällige Sortierung" }
    ],
    languages: [
      { value: "de", title: "Deutsch" },
      { value: "en", title: "Englisch" }
    ],
    seasons: [
      { value: "summer", title: "Sommer" },
      { value: "winter", title: "Winter" },
      { value: "principalSeason", title: "Gemäß Mandanten-Saisonzeit" }
    ],
    pictureSeasons: [
      { value: "all", title: "Alle Bilder" },
      { value: "summer", title: "Sommer & Ganzjährig" },
      { value: "winter", title: "Winter & Ganzjährig" },
      { value: "principalSeason", title: "Gemäß Mandanten-Saisonzeit" }
    ],
    outputFormats: [
      { value: "listView", title: "Liste" },
      { value: "cardView", title: "Galerie" }
    ],
    overlayModes: [
      { value: "dark", title: "Dark-Mode" },
      { value: "light", title: "Light-Mode" },
      { value: "overlay", title: "Overlay-Mode" }
    ],
    listStyles: [
      { value: "listMap", text: "Liste mit Karte" },
      { value: "gridMap", text: "Grid mit Karte" },
      { value: "compactMap", text: "Kompakte Liste mit Karte" },
      { value: "list", text: "Liste ohne Karte" },
      { value: "grid", text: "Grid ohne Karte" },
      { value: "compact", text: "Kompakte Liste ohne Karte" }
    ],
    pictureArray: [],
    snackbarText: "",
    snackbarColor: "",
    showSummary: false,
    snackbar: false,
    showIframePreview: {
      list: false,
      teaser: false,
      contentTeaser: false
    },
    refreshIframePreview: {
      list: 0,
      teaser: 0,
      contentTeaser: 0
    }
  }),
  props: ["apiKey", "apiKeyValid", "pois", "allPois"],
  async mounted() {
    this.selectedMappingFields = this.apiKey.includedMappingFields;
    await this.fetchData();
    this.initGlobalCopyToClipboard();
    if (this.apiKey.exhibitionEventId?.length) {
      const exhibitionEvent = await this.getEvent(
        this.apiKey.exhibitionEventId
      );

      this.foundEvents.push(exhibitionEvent);
    }
  },
  computed: {
    isAdmin() {
      return this.$store.state.currentUser?.scopes.includes("role:admin");
    },
    isReadOnly() {
      if (this.isAdmin) {
        return false;
      }
      if (this.apiKey.userPermission === "edit") {
        return false;
      }
      return true;
    },
    exhibitionEvents() {
      return this.foundEvents;
    },
    mappingFields() {
      return this.$store.state.mappingFields;
    },
    availableMappingFields() {
      return this.mappingFields.filter(
        mField => !this.apiKey.includedMappingFields.includes(mField._id)
      );
    },
    categoryGroups() {
      return this.$store.state.categoryGroups;
    },
    categoryTypes() {
      return this.$store.state.categoryTypes;
    },
    regions() {
      return this.$store.state.regions;
    },
    cities() {
      return this.$store.state.cities;
    },
    categories() {
      return _.flatten(this.categoryTypes?.map(ct => ct.categories));
    },
    featureGroups() {
      return this.$store.state.featureGroups;
    },
    features() {
      return _.flatten(this.$store.state.featureGroups.map(fg => fg.features));
    },
    fallbackPicture: {
      get() {
        return this.apiKey.fallbackPicture;
      },
      set(newVal) {
        this.pictureArray = [];
        this.pictureArray.push(newVal);
        this.apiKey.fallbackPicture = newVal;
      }
    },
    isValid: {
      get() {
        return this.apiKeyValid;
      },
      set(value) {
        this.$emit("setApiKeyValid", value);
      }
    },
    // Update computed property only when 'refreshIframePreview' is increased in debounced function 'setCodePreview'
    // as to not reload the iframe for every single color change (e.g. by selecting a color via the color-picker canvas).
    iframeSrcListDebounced() {
      if (++this.refreshIframePreview.list) {
        return this.getIframeSrc("pois/", this.apiKey.newWidget);
      }
    },
    iframeSrcTeaserDebounced() {
      if (++this.refreshIframePreview.teaser) {
        return this.getIframeSrc("teaser/", this.apiKey.newWidget);
      }
    },
    iframeSrcContentTeaserDebounced() {
      if (++this.refreshIframePreview.contentTeaser) {
        return this.getIframeSrc("content-teaser/", this.apiKey.newWidget);
      }
    },
    logUrl() {
      return `${env.poi_api_client_url}log/${this.apiKey._id}`;
    }
  },
  methods: {
    getListHTML,
    getTeaserHTML,
    getDetailHTML,
    getNewListHTML,
    getNewTeaserHTML,
    getNewContentTeaserHTML,
    getNewDetailHTML,
    initGlobalCopyToClipboard,
    escapeHTML,
    isValidSVG(value) {
      return (
        !value?.length ||
        (value.includes("<svg") &&
          value.includes("</svg>") &&
          value.length > 20)
      );
    },
    addCustomFeatureGroup() {
      if (this.isReadOnly) {
        return;
      }
      if (!this.apiKey.customFeatureGroups?.length)
        this.apiKey.customFeatureGroups = [];

      this.apiKey.customFeatureGroups.push({
        label: "",
        featureIds: []
      });
      this.$emit("refreshApiKey");
    },
    removeCustomFeatureGroup(item) {
      if (this.isReadOnly) {
        return;
      }
      this.apiKey.customFeatureGroups.remove(item);
      this.$emit("refreshApiKey");
    },
    moveCustomFeatureGroup(oldIndex, newIndex) {
      if (this.isReadOnly) {
        return;
      }
      const oldItem = this.apiKey.customFeatureGroups[oldIndex];

      this.apiKey.customFeatureGroups[
        oldIndex
      ] = this.apiKey.customFeatureGroups[newIndex];
      this.apiKey.customFeatureGroups[newIndex] = oldItem;

      this.$emit("refreshApiKey");
    },
    addCustomCategoryGroup() {
      if (this.isReadOnly) {
        return;
      }
      if (!this.apiKey.customCategoryGroups?.length)
        this.apiKey.customCategoryGroups = [];

      this.apiKey.customCategoryGroups.push({
        label: "",
        categoryIds: []
      });
      this.$emit("refreshApiKey");
    },
    removeCustomCategoryGroup(item) {
      if (this.isReadOnly) {
        return;
      }
      this.apiKey.customCategoryGroups.remove(item);
      this.$emit("refreshApiKey");
    },
    moveCustomCategoryGroup(oldIndex, newIndex) {
      if (this.isReadOnly) {
        return;
      }
      const oldItem = this.apiKey.customCategoryGroups[oldIndex];

      this.apiKey.customCategoryGroups[
        oldIndex
      ] = this.apiKey.customCategoryGroups[newIndex];
      this.apiKey.customCategoryGroups[newIndex] = oldItem;

      this.$emit("refreshApiKey");
    },
    async fetchData() {
      try {
        await this.$store.dispatch("getCategoryGroups");
        await this.$store.dispatch("getCategoryTypes");
        await this.$store.dispatch("getFeatureGroupsForPois");
        await this.$store.dispatch("getCities");
      } catch (ex) {
        this.createError(ex);
      }
    },
    addMappingField(mappingField) {
      if (this.isReadOnly) {
        return;
      }
      this.apiKey.includedMappingFields.push(mappingField);
    },
    removeMappingField(mappingField) {
      if (this.isReadOnly) {
        return;
      }
      this.apiKey.includedMappingFields = this.apiKey.includedMappingFields.filter(
        mField => mField !== mappingField
      );
    },
    getMappingFieldTitle(mappingField) {
      return this.mappingFields.find(mField => mField._id === mappingField)
        .title;
    },
    clearInput(text, filteredItems) {
      if (filteredItems?.length === 1) this[text] = "";
    },
    async addDropFile(e) {
      if (this.isReadOnly) {
        return;
      }
      if (typeof this.apiKey.fallbackPicture !== "object")
        this.apiKey.fallbackPicture = {};
      const file = e?.dataTransfer?.files ?? e ?? false;
      if (file) {
        file.dataUrl = await this.readFileUrl(file);
        file.title = file.name;
        this.apiKey.fallbackPicture = file;
        await this.$emit("saveKey", false);
      } else this.apiKey.fallbackPicture = {};
    },
    async addDropFileBasePicture(e) {
      if (this.isReadOnly) {
        return;
      }
      if (typeof this.apiKey.salesSettings.defaultPicture !== "object")
        this.apiKey.salesSettings.defaultPicture = {};
      const file = e?.dataTransfer?.files ?? e ?? false;
      if (file) {
        file.dataUrl = await this.readFileUrl(file);
        file.title = file.name;
        this.apiKey.salesSettings.defaultPicture = file;
        await this.$emit("saveKey", false);
      } else this.apiKey.salesSettings.defaultPicture = {};
    },
    async confirmRemoveImage(isFallbackPicture) {
      if (this.isReadOnly) {
        return;
      }
      const isConfirmed = await this.$confirm(
        "Sind Sie sicher, dass Sie das Bild löschen möchten?",
        confirmWarningConfig
      );
      if (isConfirmed) {
        await this.removeFile(isFallbackPicture);
      }
    },
    async removeFile(isFallbackPicture) {
      let deleted = null;
      if (isFallbackPicture)
        deleted = destroyFile("pois", this.apiKey.fallbackPicture.id);
      else
        deleted = destroyFile(
          "pois",
          this.apiKey.salesSettings.defaultPicture.id
        );
      if (!deleted) {
        await this.$store.dispatch("createError", {
          text: "Bild konnte nicht gelöscht/gefunden werden!"
        });
      }
      this.apiKey.fallbackPicture = {};
      await this.$emit("saveKey", false);
    },
    readFileUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = ({ target }) => {
          if (target?.result) resolve(target.result);
          else reject("Unable to read File");
        };
        reader.readAsDataURL(file);
      });
    },
    createError(error) {
      this.snackbarText =
        "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später noch einmal";
      this.snackbarColor = "#c55c52";
      this.snackbar = true;
      console.error(error);
    },
    principalId() {
      this.$store.state.selectedPrincipal?.id;
    },
    setColorAndCodePreview(colorKey, colorValue) {
      if (colorKey && colorValue) {
        this.$set(this.apiKey.color, colorKey, colorValue);
      }
      this.$nextTick(() => {
        this.$forceUpdate();
        this.setCodePreview();
      });
    },
    setCodePreview: _.debounce(function() {
      const codePreviewContainers = document.querySelectorAll(
        ".widget-code-preview"
      );
      if (!codePreviewContainers?.length) {
        return;
      }
      Array.from(codePreviewContainers).forEach(codePreviewContainer => {
        const codePreviewElement = codePreviewContainer.querySelector("pre");

        const codePreviewFor =
          codePreviewContainer.attributes["data-code-preview-for"].value;
        const isNewWidget = this.apiKey.newWidget;
        let innerHTML;

        switch (codePreviewFor) {
          case "list":
            innerHTML = isNewWidget
              ? this.getNewListHTML(this.apiKey)
              : this.getListHTML(this.apiKey);
            break;
          case "teaser":
            innerHTML = isNewWidget
              ? this.getNewTeaserHTML(this.apiKey)
              : this.getTeaserHTML(this.apiKey);
            break;
          case "detail":
            innerHTML = isNewWidget
              ? this.getNewDetailHTML(this.apiKey)
              : this.getDetailHTML(this.apiKey);
            break;
          case "content-teaser":
            innerHTML = this.getNewContentTeaserHTML(this.apiKey);
            break;
          case "iframe-list":
            innerHTML = this.getIframeHTML("pois", this.apiKey.newWidget);
            this.refreshIframePreview.list++;
            break;
          case "iframe-teaser":
            innerHTML = this.getIframeHTML("teaser", this.apiKey.newWidget);
            this.refreshIframePreview.teaser++;
            break;
          case "iframe-content-teaser":
            innerHTML = this.getIframeHTML(
              "content-teaser",
              this.apiKey.newWidget
            );
            this.refreshIframePreview.teaser++;
            break;
          default:
            throw new Error("can not set code preview");
        }

        if (codePreviewElement) {
          codePreviewElement.remove();
        }
        codePreviewContainer.lastChild.innerHTML = this.getPreviewOuterHTML(
          innerHTML
        );
      });

      window.Prism.highlightAll();
    }, 1000),
    async getEvent(eventId) {
      const headers = getTokenHeader();
      const response = await axios.get(
        `${env.events_api_client_url}event/${eventId}`,
        {
          params: {
            principal_id: { $in: [this.$store.state.selectedPrincipal.id] }
          },
          headers
        }
      );
      return response.data;
    },
    getPreviewOuterHTML(innerHTML) {
      const randomId = (Math.random() + 1).toString(36).substring(7);
      const copyButtonHTML = `<p class="copy-code-preview"
      onClick="copyCodePreviewToClipboard('${randomId}');"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M19 21H8V7h11m0-2H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2m-3-4H4a2 2 0 0 0-2 2v14h2V3h12V1Z"/></svg>
        <span>Kopieren</span>
      </p>`;
      return `${copyButtonHTML}<pre class="language-markup" id="${randomId}"><code>${this.escapeHTML(
        innerHTML
      )}</code></pre>`;
    },
    getParamsForIframeSrc() {
      let c = "?";
      const paramsObj = {
        apiKey: this.apiKey.apiKey,
        ...this.apiKey.color
      };
      if (this.apiKey.newWidget) {
        for (const key of Object.keys(paramsObj)) {
          if (key.startsWith("map")) {
            delete paramsObj[key];
          }
        }
      }
      let params = "";
      for (const [key, value] of Object.entries(paramsObj)) {
        params += `${c}${key}=${encodeURIComponent(value)}`;
        c = "&";
      }
      return params;
    },
    getIframeSrc(listOrTeaser, newWidget) {
      if (!listOrTeaser.endsWith("/")) {
        listOrTeaser += "/";
      }
      const serviceUrl = newWidget
        ? env.pois_widget_api_url_v2
        : env.pois_widget_api_url;

      return `${serviceUrl}${listOrTeaser}${this.getParamsForIframeSrc()}`;
    },
    getIframeHTML(listOrTeaser, newWidget) {
      return `<iframe src="${this.getIframeSrc(
        listOrTeaser,
        newWidget
      )}"></iframe>`;
    },
    updateSalesFeatureValue(value, key) {
      if (Array.isArray(value)) value = value[0];

      if (this.apiKey.salesSettings) this.apiKey.salesSettings[key] = value;
    }
  },
  watch: {
    async principalId() {
      await this.fetchData();
    },
    async eventSearch(val) {
      if (this.isLoading) return;

      this.isLoading = true;

      const headers = getTokenHeader();
      const { data } = await axios.post(
        `${env.events_api_client_url}event/for_calendar`,
        {
          query: {},
          principal_id: this.$store.state.selectedPrincipal.id,
          s: val,
          distinct: true
        },
        { headers }
      );
      if (data?.results?.length) this.foundEvents = data.results;
      this.isLoading = false;
    }
  },
  components: {
    ImagePreview,
    TreeViewSelection,
    InfoDialog,
    FallbackPictureCopyrightEditView,
    draggable,
    ColorPickerButton,
    VuePrism,
    LogOverview
  }
};
